import Color from 'color';
import { lFColor, lFColor0 } from 'pages/components/LFHeader/LFHeader.styled';
import { borderColor2, leftSidePanelMotionEase } from 'pages/styles';
import styled, { keyframes } from 'styled-components';
import { cashColor, redColor } from './LogTable.styled';

const ink = '#212529';
const inkMuted = '#6c757d';

export const Anchor = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/** List icon beside the value — boxed only when this row is a start/end lock. */
export const Trigger = styled.button<{ $open?: boolean; $locked?: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  box-sizing: border-box;
  border-radius: 6px;
  border: 1px solid ${({ $locked }) => (
    $locked ? Color(lFColor0).alpha(0.35).toString() : 'transparent'
  )};
  background: ${({ $open, $locked }) => {
    if ($locked) return Color(lFColor0).alpha(0.2).toString();
    if ($open) return Color(lFColor0).alpha(0.12).toString();
    return 'transparent';
  }};
  color: ${({ $open, $locked }) => (
    $open || $locked ? lFColor : Color(lFColor).alpha(0.72).toString()
  )};
  cursor: pointer;
  line-height: 1;
  transition:
    background 160ms ${leftSidePanelMotionEase},
    color 160ms ${leftSidePanelMotionEase},
    border-color 160ms ${leftSidePanelMotionEase};

  &:hover {
    background: ${Color(lFColor0).alpha(0.22).toString()};
    color: ${lFColor};
  }
`;

const popupIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const popupOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-4px);
  }
`;

export const Popup = styled.div<{ $leaving?: boolean; $wide?: boolean }>`
  position: fixed;
  z-index: 1400;
  width: ${({ $wide }) => ($wide
    ? 'min(280px, calc(100vw - 24px))'
    : 'min(256px, calc(100vw - 24px))')};
  padding: 8px;
  background: white;
  border: 1px solid ${Color(borderColor2).lighten(0.05).toString()};
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14), 0 0 0 1px rgba(0, 0, 0, 0.04);
  animation: ${({ $leaving }) => ($leaving ? popupOut : popupIn)}
    160ms ${leftSidePanelMotionEase};
  animation-fill-mode: both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const panelIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Panel = styled.div`
  animation: ${panelIn} 160ms ${leftSidePanelMotionEase};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Divider = styled.div`
  height: 1px;
  background: ${Color(borderColor2).lighten(0.05).toString()};
  margin: 8px 2px;
`;

export const DangerOptionRow = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: none;
  border-radius: 6px;
  padding: 8px;
  background: transparent;
  color: ${ink};
  cursor: pointer;
  font-family: inherit;
  transition: background 100ms ease;

  &:hover {
    background: ${Color(redColor).alpha(0.08).toString()};
  }
`;

export const DangerOptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  margin-top: 1px;
  color: ${redColor};

  & > svg {
    font-size: 18px;
  }
`;

export const EditHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 4px 8px;
`;

export const EditTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${lFColor};
  min-width: 0;
`;

export const EditClose = styled.button`
  appearance: none;
  height: 26px;
  width: 26px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-left: auto;
  border: none;
  border-radius: 6px;
  padding: 0;
  background: transparent;
  color: ${inkMuted};
  flex-shrink: 0;
  transition: color 120ms ease, background 120ms ease;
  &:hover {
    color: ${redColor};
    background: ${Color(redColor).alpha(0.1).toString()};
  }
`;

export const EditBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 4px 4px;
`;

export const EditHint = styled.div<{ $error?: boolean }>`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $error }) => ($error ? redColor : inkMuted)};
  padding-left: 2px;
  line-height: 1.3;
`;

export const EditInputRow = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1.5px solid
    ${({ $error }) => ($error ? redColor : Color(borderColor2).darken(0.08).toString())};
  background: #fff;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  &:focus-within {
    border-color: ${({ $error }) => ($error ? redColor : lFColor0)};
    box-shadow: 0 0 0 0.2rem
      ${({ $error }) => (
    $error
      ? Color(redColor).alpha(0.16).toString()
      : Color(lFColor0).alpha(0.18).toString()
  )};
  }
`;

export const EditInput = styled.input<{ $amount: number }>`
  height: 44px;
  padding: 0 12px;
  border: 0;
  flex: 1;
  min-width: 0;
  background: transparent;
  font-weight: 700;
  font-size: 17px;
  letter-spacing: 0.4px;
  font-family: inherit;
  color: ${({ $amount }) => ($amount < 0 ? redColor : cashColor)};
  &:focus {
    outline: none;
  }
`;

export const EditSave = styled.button`
  appearance: none;
  font-size: 13px;
  height: 44px;
  display: flex;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
  width: max-content;
  flex-shrink: 0;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #fff;
  background: ${lFColor};
  border: none;
  font-family: inherit;
  cursor: pointer;
  transition: background 120ms ease;
  &:hover {
    background: ${lFColor0};
  }
`;
